import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PulseLoader from "react-spinners/PulseLoader";
import { TextInput, Image } from "hesus-ui-kit";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import addressIcon from "@icons/ic-address.svg";
import phoneIcon from "@icons/ic-phone.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import WarehouseTrucks from "./components/WarehouseTrucks/WarehouseTrucks";
import { WAREHOUSE_LIST_ENDPOINT } from "@utils/constants";
import { loadAllWarehouse } from "./WarehouseList.actionCreators";
import { setAllWarehouseLoadingState } from "./WarehouseList.slice";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";
import WarehouseAction from "./components/WarehouseAction/WarehouseAction";
import { UndefinedMessageError, searchInputStyles } from "@utils/extra-style";

import {
  OuterWrapper,
  FlexRow,
  FilterFlexRow,
  SectionContainer,
  GridRow,
  ProductContainer,
  ContentInfo,
  ButtonWrapper,
  CreateButton,
  FilterOuter,
  GlobalSearch,
  GridLoaderItem,
} from "./WarehouseList.styled";

import "./WarehouseList.scss";
import { textReplace, address } from "@utils/utils";

const ViewData = (props) => {
  const {
    t,
    carrierId,
    companyId,
    countryId,
    companyStatus,
    companyName,
    createWarehouse,
    isRefreshList,
    updateRefeshStatus,
  } = props;

  const history = useNavigate();
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;
  const [activeStatus, setActiveStatus] = useState(1);
  const [removalStatus, setRemovalStatus] = useState(1);
  const [supplyStatus, setSupplyStatus] = useState(0);
  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(9);
  const [search, setSearch] = useState("");
  const [sort, setSortOption] = useState({ dir: "asc", column: "id" });

  let dataURL = textReplace(WAREHOUSE_LIST_ENDPOINT, "{companyId}", companyId);
  dataURL = textReplace(dataURL, "{haulierId}", carrierId);

  const dispatch = useDispatch();

  const warehouseListByIds = useSelector((state) => state.warehouseList.byId);
  const warehouseListAllIds = useSelector(
    (state) => state.warehouseList.allIds
  );

  const filteredRecords = useSelector(
    (state) => state.warehouseList.filteredRecords
  );

  const isWarehouseListLoading = useSelector(
    (state) => state.warehouseList.isLoading
  );

  useEffect(() => {
    setLoading(isWarehouseListLoading);
  }, [isWarehouseListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    search,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    if (carrierId !== undefined) {
      // save request data,
      setRequestData({ pageSize, pageStart, search, sort, appliedFilters });
      // Set the loading state
      setLoading(true);

      dispatch(setAllWarehouseLoadingState());
      dispatch(
        loadAllWarehouse({
          dataURL,
          pageSize,
          currentPage,
          sort,
          search,
          appliedFilters,
          status: 1,
        })
      );
    }
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  useEffect(() => {
    if (!loading) {
      fetchRecords({
        pageSize,
        pageStart,
        search,
        sort,
        appliedFilters: {
          status: activeStatus,
          removal: removalStatus,
          supply: supplyStatus,
        },
        currentPage,
      });
    }
  }, [
    currentPage,
    pageSize,
    pageStart,
    activeStatus,
    removalStatus,
    supplyStatus,
    search,
    isRefreshDataAgain,
  ]);

  useEffect(() => {
    if (warehouseListAllIds.length > 0 && carrierId !== undefined) {
      const responseData = warehouseListAllIds.map((id) => {
        const tempData = { ...warehouseListByIds[id] };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, warehouseListAllIds, warehouseListByIds, filteredRecords]);

  const changeStatus = () => {
    const newStatus = activeStatus === 1 ? 2 : 1;
    setActiveStatus(newStatus);
    setSortOption(
      newStatus
        ? { dir: "desc", column: "status" }
        : { dir: "desc", column: "id" }
    );

    setCurrentPage(1);
  };

  const globalSearchFilter = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const getFilterTrucks = (trucks) => {
    if (trucks !== null) {
      return trucks.filter((truck) => truck.status === true);
    }
    return trucks;
  };

  return (
    <>
      <OuterWrapper>
        <FlexRow>
          <SectionContainer>
            {carrierId === undefined && (
              <UndefinedMessageError>
                {t("COMPANY_WAREHOUSES_MISSING_CARRIER_IN_DB")}
              </UndefinedMessageError>
            )}
            {loading && (
              <GridRow column="1fr">
                <GridLoaderItem>
                  <PulseLoader width="100%" color="#1bace4" />
                </GridLoaderItem>
              </GridRow>
            )}
            {!loading && carrierId !== undefined && (
              <>
                <div>
                  <FilterFlexRow>
                    <FilterOuter>
                      <div className="status__filters-wrapper">
                        <Image
                          src={activeStatus === 1 ? enableIcon : disableIcon}
                          alt={t("WAREHOUSES_FILTER_ACTIVE_ONLY")}
                          title={t("WAREHOUSES_FILTER_ACTIVE_ONLY")}
                          className="warehouses-list__image-icon"
                          styles={{ marginLeft: "7px" }}
                          onClick={() => changeStatus()}
                        />
                        <label className="label warehouses-list__active-label">
                          {t("WAREHOUSES_FILTER_ACTIVE_ONLY")}
                        </label>
                      </div>
                    </FilterOuter>
                    <GlobalSearch>
                      <div className="search-box">
                        <TextInput
                          id="warehouse-list-search"
                          type="search"
                          name="global-filter"
                          inputClass="search-box-default-input"
                          value={search || ""}
                          onChange={(e) => globalSearchFilter(e)}
                          placeholder=""
                          inputStyles={searchInputStyles}
                        />
                      </div>
                    </GlobalSearch>
                    <ButtonWrapper>
                      <CreateButton onClick={createWarehouse}>{`${t(
                        "ACTION_NEW_WAREHOUSE"
                      )}`}</CreateButton>
                    </ButtonWrapper>
                  </FilterFlexRow>
                </div>
                <div>
                  <GridRow column="repeat(auto-fit, minmax(400px, 520px));">
                    {data.length !== "" &&
                      data.map((data, index) => {
                        return (
                          <ProductContainer key={index}>
                            <GridRow column="70% auto" gap="0rem">
                              <span>
                                <Link
                                  to={`/company/view/${companyId}/warehouse/view/${data.id}`}
                                  className="warehouses-list__link"
                                  style={{
                                    textDecoration: "none",
                                    opacity: !data.status ? ".2" : "1",
                                  }}
                                  width="220%"
                                >
                                  {"# " + data.id + " - " + data.name}
                                </Link>
                              </span>
                              <WarehouseAction
                                data={data}
                                companyId={companyId}
                                onRefreshData={setRefreshState}
                                companyStatus={companyStatus}
                                carrierId={carrierId}
                                countryId={countryId}
                                updateRefeshStatus={updateRefeshStatus}
                              />
                            </GridRow>
                            <GridRow
                              column="30px auto"
                              opacity={!data.status ? ".2" : 1}
                            >
                              <Image
                                src={addressIcon}
                                alt="address"
                                className="warehouses-list__image-icon"
                                styles={{ marginLeft: "7px" }}
                              />
                              <ContentInfo
                                dangerouslySetInnerHTML={{
                                  __html: address(
                                    countryId,
                                    data.address,
                                    countryList
                                  ),
                                }}
                              ></ContentInfo>
                            </GridRow>
                            <GridRow
                              column="30px auto"
                              opacity={!data.status ? ".2" : 1}
                              alignItems="center"
                            >
                              <>
                                <Image
                                  src={phoneIcon}
                                  alt="phone"
                                  className="warehouses-list__image-icon"
                                />
                                <ContentInfo>
                                  {data.phone !== null && data.phone !== ""
                                    ? data.phone
                                    : "-"}
                                </ContentInfo>
                              </>
                            </GridRow>
                            <hr />
                            <WarehouseTrucks
                              trucks={getFilterTrucks(data.trucks)}
                              status={data.status}
                            />
                          </ProductContainer>
                        );
                      })}
                  </GridRow>
                  {(data.length === "" || data.length === 0) && (
                    <GridRow column="1fr">{t("NO_WAREHOUSES")}</GridRow>
                  )}
                </div>
              </>
            )}
          </SectionContainer>
        </FlexRow>

        {data.length !== 0 && (
          <PaginationData
            pageCount={parseInt(pageCount, 10)}
            pageSize={requestData.pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageStart={pageStart}
            setPageStart={setPageStart}
            setPageSize={setPageSize}
          />
        )}
      </OuterWrapper>
    </>
  );
};

ViewData.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
};

export default withTranslation()(ViewData);
